import dayjs from "dayjs";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { GenerateAiTextButton } from "./GenerateAiTextButton";
import { useCreateLocalPostReducer } from "./hooks/useCreateLocalPostReducer";
import { TopicTypeSelect } from "./TopicTypeSelect";
import {
  ActionType,
  CreateLocalPostState,
  LocalPostTopicType,
  generatePayloadToGeneratePostTextWithGemini,
  isCreateLocalPostStateValid
} from "./utils";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import { OptionalRender } from "../../Common/OptionalRender";
import Checkbox from "../../FormElement/Checkbox";
import { DateTimeIntervalInput } from "../../FormElement/DateTimeIntervalInput";
import DropdownSelect from "../../FormElement/DropdownSelect";
import InputBox from "../../FormElement/InputBox";
import InputTextArea from "../../FormElement/InputTextarea";
import { MediaUpload } from "../../FormElement/MediaUpload";
import ModalConfirm from "../../ModalConfirm";
import { ModalPreviewLocalPost } from "../ModalCreateLocalPost/ModalPreviewLocalPost";
import "./styles.scss";

export interface ICreateLocalPostFormProps {
  showPostScheduleTypeRadio?: boolean;
  locationData: LocationData | undefined;
  post: (
    value: CreateLocalPostState,
    postScheduleType: "immediate" | "schedule",
    scheduleAt?: string
  ) => void;
  enablePostToInstagram?: boolean;
  canGenerateAiText?: boolean;
  onClose?: () => void;
  locationNames: string[];
}

export const CreateLocalPostForm: React.FunctionComponent<
  ICreateLocalPostFormProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `analysisCollectionsPage.modalCreateLocalPost.createLocalPostForm.${key}`
    );

  const {
    locationData,
    post,
    onClose,
    canGenerateAiText,
    enablePostToInstagram,
    showPostScheduleTypeRadio = false
  } = props;
  const hasPrimaryPhone = locationData?.pickup_fields?.has_primary_phone;
  const canPostInstagram =
    (locationData?.locations_instagram_integrations?.length ?? 0) > 0;

  const [shownModalConfirm, setShownModalConfirm] = useState<boolean>(false);
  const [shownModalPreview, setShownModalPreview] = useState<boolean>(false);

  const {
    state,
    onChangeTopicType,
    onChangeMedia,
    onChangeMediaError,
    onChangeEventTitle,
    onChangeEventSchedule,
    onChangeEventScheduleError,
    onChangeBody,
    onChangeOfferCouponCode,
    onChangeOfferRedeemOnlineUrl,
    onChangeOfferTermsAndConditions,
    onChangeCallToActionType,
    onChangeCallToActionUrl,
    onChangePostToInstagram,
    onChangePostToGbp,
    onChangePostBodyText,
    onChangeGeminiPrompt
  } = useCreateLocalPostReducer();

  const {
    media,
    mediaError,
    text,
    messageTextError,
    callToAction,
    topicType,
    event,
    offer,
    geminiPrompt
  } = state;

  const missingImageInstagram = media.length === 0 && canPostInstagram;

  const onTriggerGenerateText = async () => {
    if (!canGenerateAiText) {
      return;
    }

    const data = await generatePayloadToGeneratePostTextWithGemini(
      state,
      locationData?.id
    );

    ReactGA.event({
      category: "create-local-post",
      action: "Generate description with AI",
      label: state.topicType
    });

    const response = await DataSvc.generateLocalPostTextWithGemini(data);
    onChangePostBodyText(response);
  };

  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
  const [postScheduleType, setPostScheduleType] = useState<
    "immediate" | "schedule"
  >("immediate");
  const [scheduledDate, setScheduledDate] = useState<string>(tomorrow);
  const [scheduledTime, setScheduledTime] = useState<string>("12:00");

  const handlePost = () => {
    let scheduleAt: string | undefined = undefined;
    if (postScheduleType === "schedule") {
      const localDateTime = `${scheduledDate}T${scheduledTime}`;
      scheduleAt = new Date(localDateTime).toISOString();
    }
    post(state, postScheduleType, scheduleAt);
  };

  return (
    <div>
      <div className="create-local-post-form">
        <TopicTypeSelect
          changeTopicType={onChangeTopicType}
          locationData={locationData}
          topicType={topicType}
        />
        <div className="media-upload-wrapper">
          <h3>{t("add_photo")}</h3>
          <MediaUpload
            files={media}
            changeFiles={onChangeMedia}
            error={mediaError}
            changeError={onChangeMediaError}
            maxFiles={1}
            maxSizeMiB={10}
            minWidth={400}
            minHeight={300}
          />
        </div>
        {(topicType === LocalPostTopicType.EVENT ||
          topicType === LocalPostTopicType.OFFER) && (
          <div className="event">
            <h3>{t(`title.${topicType}`)}</h3>
            <InputBox
              name={"eventTitle"}
              value={event.title}
              placeholder={t(`title_placeholder.${topicType}`)}
              onChange={onChangeEventTitle}
              errorLabel={event.titleError}
              classNameContainer="margin-bottom-30"
            />
            <DateTimeIntervalInput
              value={event.schedule}
              onChange={onChangeEventSchedule}
              error={event.scheduleError}
              onChangeError={onChangeEventScheduleError}
              hideTimeOfDay={topicType === LocalPostTopicType.OFFER}
              minDate={MIN_DATE}
              maxDate={GET_MAX_DATE()}
            />
          </div>
        )}
        {canGenerateAiText && (
          <>
            <h3>{t(`extra_instruction_for_ai_title`)}</h3>
            <InputTextArea
              name="geminiPrompt"
              value={geminiPrompt}
              placeholder={t(`extra_instruction_for_ai_placeholder`)}
              classNameContainer={"message-body-input"}
              onChange={onChangeGeminiPrompt}
              maxChars={1000}
            />
            <GenerateAiTextButton
              localPostState={state}
              onClick={onTriggerGenerateText}
            />
          </>
        )}
        <div className="message-body">
          <h3>{t(`message_body.${topicType}`)}</h3>
          <InputTextArea
            name="messageText"
            value={text}
            placeholder={t(`message_body_placeholder.${topicType}`)}
            classNameContainer={"message-body-input margin-bottom-30"}
            errorLabel={messageTextError}
            onChange={onChangeBody}
            maxChars={1500}
            showChars={true}
          />
        </div>
        {topicType === LocalPostTopicType.OFFER && (
          <div className="offer">
            <h3>{t("offer.coupon_code")}</h3>
            <InputBox
              name={"couponCode"}
              value={offer.couponCode}
              placeholder={t("offer.coupon_code_placeholder")}
              onChange={onChangeOfferCouponCode}
              errorLabel={offer.couponCodeError}
              classNameContainer="margin-bottom-30"
            />
            <h3>{t("offer.redeem_online_url")}</h3>
            <InputBox
              name={"redeemOnlineUrl"}
              value={offer.redeemOnlineUrl}
              placeholder={t("offer.redeem_online_url_placeholder")}
              onChange={onChangeOfferRedeemOnlineUrl}
              errorLabel={offer.redeemOnlineUrlError}
              classNameContainer="margin-bottom-30"
            />
            <h3>{t("offer.terms_and_conditions")}</h3>
            <div className="message-body">
              <InputTextArea
                name="termsAndConditions"
                value={offer.termsConditions}
                placeholder={t("offer.terms_and_conditions_placeholder")}
                classNameContainer={"message-body-input margin-bottom-30"}
                errorLabel={offer.termsAndConditionsError}
                onChange={onChangeOfferTermsAndConditions}
                maxChars={5000}
                showChars={true}
              />
            </div>
          </div>
        )}
        {topicType !== LocalPostTopicType.OFFER && (
          <div className="call-to-action">
            <h3>{t("add_button")}</h3>
            <DropdownSelect
              isTranslation={true}
              translationKey="analysisCollectionsPage.modalCreateLocalPost.createLocalPostForm.actionTypes"
              value={callToAction.actionType}
              items={
                hasPrimaryPhone
                  ? Object.values(ActionType).map((item) => item.toString())
                  : Object.values(ActionType)
                      .filter((item) => item !== ActionType.CALL)
                      .map((item) => item.toString())
              }
              onChange={onChangeCallToActionType}
            />
            {callToAction.actionType !== ActionType.ACTION_TYPE_UNSPECIFIED &&
              callToAction.actionType !== ActionType.CALL && (
                <div className="url-wrapper">
                  <h3>{t("url")}</h3>
                  <InputBox
                    value={callToAction.url}
                    onChange={onChangeCallToActionUrl}
                    errorLabel={callToAction.urlError}
                    placeholder={t("url_placeholder")}
                  />
                </div>
              )}
          </div>
        )}
        <h3>{t("post_platforms")}</h3>
        <div className="post-to-gbp-wrapper">
          <Checkbox
            checked={state.postToGbp}
            onChange={onChangePostToGbp}
            label={t("post_to_gbp")}
            id={"post-to-gbp"}
          />
        </div>
        {enablePostToInstagram && (
          <div className="post-to-instagram-wrapper">
            <div className="post-to-instagram">
              <Checkbox
                checked={state.postToInstagram}
                disabled={media.length === 0 || !canPostInstagram}
                errorLabel={
                  !canPostInstagram
                    ? t("not_connected_to_instagram")
                    : missingImageInstagram
                    ? t("post_to_instagram_error")
                    : undefined
                }
                onChange={onChangePostToInstagram}
                label={t("post_to_instagram")}
                id={"post-to-instagram"}
              />
              {state.postToInstagram && (
                <span className="post-to-instagram-call-to-action-note">
                  {topicType === LocalPostTopicType.OFFER
                    ? t("post_to_instagram_terms_and_conditions_note")
                    : t("post_to_instagram_call_to_action_note")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      <OptionalRender mustBeTrue={showPostScheduleTypeRadio}>
        <div className="create-local-post-radio-row">
          <label
            className={`create-local-post-radio-box${
              postScheduleType === "immediate" ? " active" : ""
            }`}
            htmlFor="post-immediate"
          >
            <input
              type="radio"
              id="post-immediate"
              name="immediate"
              checked={postScheduleType === "immediate"}
              onChange={() => setPostScheduleType("immediate")}
            />
            <span className="create-local-post-radio-title">
              {t("post_immediately")}
            </span>
          </label>
          <label
            className={`create-local-post-radio-box${
              postScheduleType === "schedule" ? " active" : ""
            }`}
            htmlFor="post-schedule"
          >
            <input
              type="radio"
              id="post-schedule"
              name="schedule"
              checked={postScheduleType === "schedule"}
              onChange={() => setPostScheduleType("schedule")}
            />
            <span className="create-local-post-radio-title">
              {t("schedule_future_post")}
            </span>
            {postScheduleType === "schedule" && (
              <div className="create-local-post-schedule-inputs">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="create-local-post-date-input"
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="create-local-post-time-input"
                />
              </div>
            )}
          </label>
        </div>
      </OptionalRender>
      <div className="create-local-post-warning">
        <span>{t("post_warning")}</span>
      </div>
      <div className="create-local-post-bottom-btns bottom-btns">
        <div>
          {onClose && (
            <button className="btn btn-border" onClick={onClose}>
              {t("cancel")}
            </button>
          )}
        </div>
        <div>
          <button
            className={`btn btn-border ${
              isCreateLocalPostStateValid(state) ? "" : "disabled"
            }`}
            onClick={() => {
              setShownModalPreview(true);
            }}
          >
            {t("preview")}
          </button>
          <button
            className={`btn btn-blue ${
              isCreateLocalPostStateValid(state) ? "" : "disabled"
            }`}
            onClick={() => {
              setShownModalConfirm(true);
            }}
            type="button"
          >
            {t("create")}
          </button>
        </div>
      </div>
      {shownModalPreview && (
        <ModalPreviewLocalPost
          onClose={() => {
            setShownModalPreview(false);
          }}
          localPost={state}
          locationData={locationData}
          setShownModalConfirm={setShownModalConfirm}
        />
      )}
      {shownModalConfirm && (
        <ModalConfirm
          title={"please_confirm_to_create_local_post"}
          cancelLabel={"cancel"}
          confirmLabel={"post"}
          onClose={() => {
            setShownModalConfirm(false);
          }}
          onConfirm={() => {
            handlePost();
            setShownModalConfirm(false);
          }}
        >
          <span>{t("post_to_these_platforms")}</span>
          <ul className="location-names-list">
            {state.postToGbp && <li>{t("gbp")}</li>}
            {state.postToInstagram && <li>{t("instagram")}</li>}
          </ul>
          <span>{t("post_to_these_locations")}</span>
          <ul className="location-names-list">
            {props.locationNames?.map((locationName) => (
              <li key={locationName}>{locationName}</li>
            ))}
          </ul>
        </ModalConfirm>
      )}
    </div>
  );
};

const MIN_DATE = dayjs("1970-01-01");
const GET_MAX_DATE = () => {
  const now = dayjs();
  return now.add(1, "year");
};
